import { useLazyQuery } from '@apollo/client';
import { useDealPromptFeatureFlaggedFilters } from '@openx/components-deal-prompt/lib/hooks/useDealPromptFeatureFlaggedFilters.ts';
import { buildSearchQueryInput } from '@openx/components-deal-prompt/lib/utils/buildSearchQueryInput.ts';
import type { UseDealPromptDomainSearchExtendedFetch } from '@openx/components-deal-prompt/lib/utils/context/type.ts';
import { mapCriteriaToFiltersBody } from '@openx/components-deal-prompt/lib/utils/filters/filterMappers.ts';
import { type DealPromptFilters, dealPromptContextTypes } from '@openx/components-deal-prompt/lib/utils/types.ts';
import type { CumulativeSegment } from '@openx/types/gqlTypes.ts';
import type { AllTableCriteria } from '@openx/types/tableCriteria.ts';
import { useCallback } from 'react';
import { InventoryDomainSearchTextDocument } from '../../../graphql/query/dealPrompt/GetInventoryDomainSearchText.generated.tsx';

export const useDealPromptDomainSearchExtendedFetch = (
  criteria: AllTableCriteria<DealPromptFilters>
): UseDealPromptDomainSearchExtendedFetch => {
  const [fetchInventoryDomainSearchText] = useLazyQuery(InventoryDomainSearchTextDocument, { fetchPolicy: 'no-cache' });
  const featureFlaggedFilters = useDealPromptFeatureFlaggedFilters();

  const fetchDealPromptDomainsExtended = useCallback(
    async ({ query }: { query: string }, abortSignal?: AbortSignal) => {
      const inputBody = {
        query: buildSearchQueryInput({ query }),
        sortBy: 'totalImpressions',
        view: 'DESC',
        excludeQuery: [],
        filters: mapCriteriaToFiltersBody(criteria, dealPromptContextTypes.DOMAINS, featureFlaggedFilters),
      };

      const result = await fetchInventoryDomainSearchText({
        variables: {
          input: inputBody,
        },
        context: {
          fetchOptions: {
            signal: abortSignal,
          },
        },
      });

      return {
        numberOfResults: result?.data?.inventoryDomainSearchText?.body?.totalRecords || 0,
        cumulativeData: (result?.data?.inventoryDomainSearchText.body?.cumulativeData ?? []) as CumulativeSegment[],
        inputs: result?.data?.inventoryDomainSearchText?.body?.input ?? {},
        _error: result?.error,
      };
    },
    [fetchInventoryDomainSearchText, criteria, featureFlaggedFilters]
  );

  return {
    fetchDealPromptDomainsExtended,
  };
};
